<script setup>
import {computed} from 'vue';
import {settingsBreadcrumbsForKey} from './breadcrumbs';

const props = defineProps({
    settings: {
        type: Object,
        required: true,
    },
    company: {
        type: Object,
        required: true,
    },
    domain: {
        type: String,
        default: '',
    },
    currentDns: {
        type: Object,
        default: null,
    },
    hasValidSetting: {
        type: Boolean,
        default: false,
    },
});

const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';
const breadcrumbs = computed(() => settingsBreadcrumbsForKey('navigation:backOffice.sidebar.myCompany.settings.communications'));
const validationInstructions = computed(() => props.company.email_mailer_validation_instructions ?? []);
const smtpValidatorError = computed(() => validationInstructions.value?.smtp_validator_error ?? null);
const confirmedDomain = computed(() => props.company.email_mailer_confirmed_domain ?? null);
const smsFromPlaceholder = computed(() => `t.ex. ${(props.company.name ?? '').slice(0, 11)}`);
</script>

<template>
    <div class="w-full">
        <frosted-bar :breadcrumbs="breadcrumbs">
            <div class="flex items-center justify-between space-x-4">
                <h1>{{ _mft('navigation:backOffice.sidebar.myCompany.settings.communications') }}</h1>
            </div>
        </frosted-bar>

        <div class="mt-8">
            <template v-if="!props.hasValidSetting">
                <p class="mb-1 text-base text-center">
                    <mf-translate domainkey="crm:settings.youNeedSettingsForOneCommunicationType"></mf-translate>
                </p>
                <p class="text-xs italic text-center">
                    <mf-translate domainkey="crm:settings.smsIsQuick"></mf-translate>
                </p>
            </template>

            <card-basic class="max-w-2xl mx-auto">
                <template #header>
                    <h2 class="w-full font-semibold text-gray-900">
                        <mf-translate domainkey="crm:emailSettings.title"></mf-translate>
                    </h2>
                </template>

                <template #body>
                    <crm-communications-email-settings-form
                        :mailer-type="props.settings.email_mailer_type"
                        :mailer-from="props.settings.email_mailer_from"
                        :mailer-from-example="props.company.user_email"
                        :mailer-from-confirmed-domain-id="props.settings.email_mailer_from_confirmed_domain_id"
                        :smtp-server-address="props.settings.smtp_server_address"
                        :smtp-server-port="props.settings.smtp_server_port"
                        :smtp-server-username="props.settings.smtp_server_username"
                        :smtp-server-password="props.settings.smtp_server_password"
                    >
                        <template #mail-validation-status>
                            <p v-if="props.company.email_mailer_validated" class="text-right text-myflowGreen">
                                <i class="text-myflowGreen fas fa-check-circle"></i>
                                <mf-translate domainkey="crm:emailSettings.valid"></mf-translate>
                            </p>

                            <template v-else-if="props.settings.email_mailer_type === 'smtp'">
                                <p class="italic text-myflowRed">
                                    <mf-translate domainkey="crm:emailSettings.invalid"></mf-translate>
                                </p>

                                <hr class="my-3">

                                <template v-if="smtpValidatorError">
                                    <p>
                                        <mf-translate domainkey="crm:emailSettings.invalid.error"></mf-translate>
                                    </p>
                                    <code class="text-xs">
                                        {{ smtpValidatorError }}
                                    </code>
                                </template>
                            </template>

                            <template v-else-if="props.settings.email_mailer_type === 'mandrill+myflow'">
                                <p class="italic text-myflowRed">
                                    <mf-translate domainkey="crm:emailSettings.furtherValidationRequired"></mf-translate>
                                </p>

                                <hr class="my-3">

                                <template v-if="Array.isArray(validationInstructions) && validationInstructions.length > 0 && confirmedDomain">
                                    <p>
                                        <mf-translate domainkey="crm:emailSettings.mandrillConfiguration.introForDomain"></mf-translate>
                                        <code>{{ confirmedDomain.domain }}</code>:
                                    </p>

                                    <ul>
                                        <li
                                            v-for="(instruction, index) in validationInstructions"
                                            :key="index"
                                            class="flex items-start gap-2 mb-3"
                                        >
                                            <div class="flex-shrink">
                                                <span v-if="instruction.status">OK</span>
                                                <span v-else>Missing</span>
                                            </div>
                                            <div class="mb-2">
                                                <p>
                                                    <code class="font-bold select-all">
                                                        {{ instruction.record_domain_prefix }}{{ confirmedDomain.domain }}
                                                    </code>
                                                    <mf-translate domainkey="crm:emailSettings.mandrillConfiguration.ofDnsRecordType"></mf-translate>
                                                    <code class="select-all">
                                                        {{ instruction.record_type }}
                                                    </code>
                                                </p>

                                                <p v-if="instruction.record_must_be" class="mb-1">
                                                    <mf-translate domainkey="crm:emailSettings.mandrillConfiguration.addDnsRecordOfType"></mf-translate>
                                                    <code class="select-all">{{ instruction.record_type }}</code>
                                                    <mf-translate domainkey="crm:emailSettings.mandrillConfiguration.addDnsRecordWithValue"></mf-translate>:
                                                </p>
                                                <pre v-if="instruction.record_must_be"><code class="select-all">{{ instruction.record_must_be }}</code></pre>

                                                <template v-if="instruction.record_instruction">
                                                    <p class="mb-2">{{ instruction.record_instruction }}</p>
                                                    <pre
                                                        v-for="(example, exampleIndex) in instruction.record_instruction_examples"
                                                        :key="exampleIndex"
                                                    ><code class="select-all">{{ example }}</code></pre>
                                                    <p class="mt-2 text-xs">{{ instruction.record_instruction_footnote }}</p>
                                                </template>
                                            </div>
                                        </li>
                                    </ul>
                                </template>
                            </template>
                        </template>
                    </crm-communications-email-settings-form>

                    <template v-if="props.currentDns">
                        <hr class="mt-5 mb-5">

                        <b><mf-translate domainkey="crm:emailSettings.dns.unsure"></mf-translate></b><br>
                        <mf-translate-html
                            domainkey="crm:emailSettings.dns.provider"
                            :args="{ currentDnsDomain: props.currentDns.domain }"
                        ></mf-translate-html><br>
                        <template v-if="props.currentDns.records.length > 0">
                            <template
                                v-for="(record, index) in props.currentDns.records"
                                :key="index"
                            >
                                <i>{{ record.target }}</i><br>
                            </template>
                        </template>
                        <template v-else>
                            <mf-translate domainkey="crm:emailSettings.dns.noEmailServersFoundForDomain"></mf-translate>
                            <i>{{ props.domain }}</i>.
                        </template>
                    </template>
                </template>
            </card-basic>
        </div>
    </div>
</template>
